import * as React from "react"

import { AxiosError } from "axios"

import emailValid from "@podcastsoundboard/ps-lib/helpers/validation/emailValid"
import sendContactEmail from "../../../../api/contact/send"
import { useAppDispatch } from "../../../../redux"
import { addSnackbar } from "../../../../redux/snackbars"
import {
  Box,
  Grid,
  Link,
  LoadingButton,
  TextField,
  Typography,
} from "@podcastsoundboard/ps-lib/ui"

export default function ContactForm({
  setErrorMessage,
  onSend,
}: {
  setErrorMessage: (message: string) => void
  onSend: () => void
}) {
  const dispatch = useAppDispatch()

  const [submitting, setSubmitting] = React.useState(false)

  const [email, setEmail] = React.useState("")
  const [name, setName] = React.useState("")
  const [device, setDevice] = React.useState("")
  const [appVersion, setAppVersion] = React.useState("")
  const [content, setContent] = React.useState("")

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    setSubmitting(true)

    try {
      const recaptchaToken = await new Promise<string>((resolve, reject) => {
        window.grecaptcha.enterprise.ready(async () => {
          try {
            const token = await window.grecaptcha.enterprise.execute(
              window.RECAPTCHA_SITE_KEY,
              { action: "contact" },
            )
            resolve(token)
          } catch (e) {
            reject(e)
          }
        })
      })

      await sendContactEmail({
        email,
        name,
        device,
        appVersion,
        content,
        recaptchaToken,
      })
      setSubmitting(false)

      dispatch(addSnackbar({ id: "emailsent", text: "Email sent." }))

      onSend && onSend()
    } catch (_err) {
      const err = _err as AxiosError<{ error: string }>
      console.error(err)
      setSubmitting(false)
      setErrorMessage(err.response?.data?.error || err.message)
    }
  }

  const submitDisabled = !(email && content)
  const emailValidationError = email && !emailValid(email) && "Email invalid."

  return (
    <Box
      component="form"
      noValidate
      onSubmit={handleSubmit}
      className="mt-6"
      width="100%"
    >
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="body1">
            This site is protected by reCAPTCHA and the Google{" "}
            <Link href="https://policies.google.com/privacy">
              Privacy Policy
            </Link>{" "}
            and{" "}
            <Link href="https://policies.google.com/terms">
              Terms of Service
            </Link>{" "}
            apply.
          </Typography>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            required
            fullWidth
            id="email"
            label="Email Address"
            name="email"
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            id="name"
            label="Name"
            name="name"
            onChange={(e) => setName(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="device"
            label="Device"
            name="device"
            onChange={(e) => setDevice(e.target.value)}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            id="appVersion"
            label="App Version"
            name="appVersion"
            onChange={(e) => setAppVersion(e.target.value)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            required
            fullWidth
            id="content"
            label="Message"
            name="content"
            multiline
            rows={10}
            onChange={(e) => setContent(e.target.value)}
          />
        </Grid>
      </Grid>

      {emailValidationError && (
        <Typography color="error" className="mt-6">
          {emailValidationError}
        </Typography>
      )}

      <LoadingButton
        disabled={submitDisabled}
        type="submit"
        loading={submitting}
        fullWidth
        variant="contained"
        className="mt-6 mb-4"
      >
        {submitting ? "Sending..." : "Send"}
      </LoadingButton>
    </Box>
  )
}
